"use client";

import { useEffect, useRef } from "react";
import { trackAppInstallInitiated } from "@/lib/services/analytics";

/**
 * Tracks iOS Smart Banner interactions
 *
 * iOS Smart Banner is a native browser feature with limited JavaScript access.
 * We detect potential clicks by monitoring visibility changes within a short time window.
 *
 * Limitations:
 * - Cannot directly intercept banner clicks (native browser UI)
 * - Detection is probabilistic, not guaranteed
 * - May have false positives if user switches tabs for other reasons
 */
export function IOSSmartBannerTracker() {
	const bannerShownRef = useRef(false);
	const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		// Only run on iOS devices where Smart Banner is supported
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as { MSStream?: unknown }).MSStream;

		if (!isIOS) {
			return;
		}

		// Mark that banner was potentially shown (iOS decides if it shows)
		bannerShownRef.current = true;

		const handleVisibilityChange = () => {
			// If user leaves page shortly after banner shown, likely clicked it
			if (document.hidden && bannerShownRef.current) {
				// Clear any existing timer
				if (visibilityTimerRef.current) {
					clearTimeout(visibilityTimerRef.current);
				}

				// Track the potential click
				trackAppInstallInitiated("ios_smart_banner");

				// Reset flag to avoid duplicate tracking
				bannerShownRef.current = false;
			}
		};

		// Set up visibility listener
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Reset the flag after 30 seconds (if no click, assume user ignored banner)
		visibilityTimerRef.current = setTimeout(() => {
			bannerShownRef.current = false;
		}, 30000);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			if (visibilityTimerRef.current) {
				clearTimeout(visibilityTimerRef.current);
			}
		};
	}, []);

	// This component doesn't render anything
	return null;
}
